import React, { useState, useEffect, useRef } from "react"
import { Users, Building2, UserCheck, Building, ChevronDown, Check, UsersRound, Link } from "lucide-react"

const AssignmentSelector = ({ 
  value, 
  onChange, 
  users = [], 
  departments = [], 
  workflowAssignment = null,
  integrations = [],
  availableVariables = [],
}) => {
  // Normalize user IDs to integers for consistent comparison
  const normalizeIds = (ids) => (ids || []).map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id))
  
  const [isOpen, setIsOpen] = useState(false)
  const [assignmentType, setAssignmentType] = useState(value?.type || 'individual')
  const [selectedUserIds, setSelectedUserIds] = useState(normalizeIds(value?.user_ids))
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState(normalizeIds(value?.department_ids))
  const [isShared, setIsShared] = useState(value?.shared !== false)
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(value?.integration_id ?? null)
  const [selectedVariable, setSelectedVariable] = useState(value?.variable ?? '')

  const dropdownRef = useRef(null)

  const buildExternalIdValue = (overrides = {}) => ({
    type: 'external_id',
    integration_id: overrides.integration_id ?? selectedIntegrationId,
    variable: overrides.variable ?? selectedVariable,
    shared: overrides.shared ?? isShared,
  })

  const buildStandardValue = (overrides = {}) => ({
    type: overrides.type ?? assignmentType,
    user_ids: overrides.user_ids ?? selectedUserIds,
    department_ids: overrides.department_ids ?? selectedDepartmentIds,
    shared: overrides.shared ?? isShared,
  })

  // Update local state when value prop changes
  useEffect(() => {
    if (value) {
      setAssignmentType(value.type || 'individual')
      // Normalize IDs to integers for consistent comparison
      setSelectedUserIds(normalizeIds(value.user_ids))
      setSelectedDepartmentIds(normalizeIds(value.department_ids))
      setIsShared(value.shared !== false)
      setSelectedIntegrationId(value.integration_id ?? null)
      setSelectedVariable(value.variable ?? '')
    }
  }, [value])

  // Add wheel event handling for proper scrolling
  useEffect(() => {
    if (!isOpen) return

    const handleWheel = (e) => {
      // Find the scrollable container
      const scrollableContainer = e.target.closest('.assignment-selector-dropdown')
      if (scrollableContainer) {
        e.stopPropagation()
        // Don't prevent default - let the browser handle scrolling
      }
    }

    // Add event listener to the dropdown
    const dropdown = document.querySelector('.assignment-selector-dropdown')
    if (dropdown) {
      dropdown.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (dropdown) {
        dropdown.removeEventListener('wheel', handleWheel)
      }
    }
  }, [isOpen])

  // Add click-outside handler to close dropdown
  useEffect(() => {

    const handleClickOutside = (e) => {
      // Use ref instead of querySelector for more reliable detection
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        // Add a small delay to prevent immediate closing after opening
        setTimeout(() => {
          setIsOpen(false)
        }, 10)
      }
    }

    // Try multiple event types to see which one works
    const events = ['mousedown', 'click', 'touchstart']
    
    events.forEach(eventType => {
      document.addEventListener(eventType, handleClickOutside, true) // Use capture phase
    })

    return () => {
      events.forEach(eventType => {
        document.removeEventListener(eventType, handleClickOutside, true)
      })
    }
  }, [isOpen])


  const assignmentTypes = [
    {
      value: 'individual',
      label: 'Individual Users',
      description: 'Assign to specific users',
      icon: <Users className="h-4 w-4" />
    },
    {
      value: 'department',
      label: 'Department',
      description: 'Assign to all users in selected departments (includes sub-departments)',
      icon: <Building2 className="h-4 w-4" />
    },
    {
      value: 'department_manager',
      label: 'Department Managers',
      description: 'Assign to managers of selected departments',
      icon: <UserCheck className="h-4 w-4" />
    },
    {
      value: 'workflow_assignment',
      label: 'Use Workflow Assignment',
      description: 'Use the workflow\'s mass assignment settings',
      icon: <Building2 className="h-4 w-4" />
    },
    {
      value: 'external_id',
      label: 'Match External User ID',
      description: 'Assign users whose external ID matches a workflow variable',
      icon: <Link className="h-4 w-4" />
    }
  ]

  // Find department managers for selected departments
  const getDepartmentManagers = (departmentIds) => {
    if (assignmentType !== 'department_manager' || !departmentIds.length) {
      return []
    }
    
    // Find users in selected departments who have manager role
    return users.filter(user => 
      departmentIds.includes(user.department_id) && 
      user.roles && 
      user.roles.some(role => role.name === 'manager')
    ).map(user => user.id)
  }

  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type)
    
    // Clear selections when changing type
    if (type === 'individual') {
      setSelectedDepartmentIds([])
    } else if (['department', 'department_manager'].includes(type)) {
      setSelectedUserIds([])
    } else if (type === 'workflow_assignment') {
      setSelectedUserIds([])
      setSelectedDepartmentIds([])
    } else if (type === 'external_id') {
      setSelectedUserIds([])
      setSelectedDepartmentIds([])
    }

    if (type === 'external_id') {
      onChange(buildExternalIdValue({ type: 'external_id' }))
      return
    }

    // For department_manager assignment, automatically populate user_ids with managers
    let newUserIds = []
    if (type === 'department_manager' && selectedDepartmentIds.length > 0) {
      newUserIds = getDepartmentManagers(selectedDepartmentIds)
      setSelectedUserIds(newUserIds)
    } else if (type === 'individual') {
      newUserIds = selectedUserIds
    }

    // Update parent component
    const newValue = {
      type,
      user_ids: newUserIds,
      department_ids: ['department', 'department_manager'].includes(type) ? selectedDepartmentIds : [],
      shared: isShared
    }
    onChange(newValue)
  }

  const handleUserToggle = (userId) => {
    // Normalize userId to integer for consistent comparison
    const normalizedUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId
    const newUserIds = selectedUserIds.includes(normalizedUserId)
      ? selectedUserIds.filter(id => id !== normalizedUserId)
      : [...selectedUserIds, normalizedUserId]
    
    setSelectedUserIds(newUserIds)
    
    const newValue = {
      type: assignmentType,
      user_ids: newUserIds,
      department_ids: selectedDepartmentIds,
      shared: isShared
    }
    onChange(newValue)
  }

  // Check if a department has children
  const hasChildren = (departmentId) => {
    return departments.some(dept => dept.parent_id === departmentId)
  }

  // Get all child department IDs for a parent (recursively - includes all descendants)
  const getChildDepartmentIds = (parentId) => {
    const directChildren = departments.filter(dept => dept.parent_id === parentId)
    let allChildren = []
    
    directChildren.forEach(child => {
      allChildren.push(child.id)
      // Recursively get children of children
      allChildren.push(...getChildDepartmentIds(child.id))
    })
    
    return allChildren
  }

  // Handle parent department selection (with children option)
  const handleParentDepartmentToggle = (departmentId, selectChildren = false) => {
    if (selectChildren) {
      const childIds = getChildDepartmentIds(departmentId)
      const allIds = [departmentId, ...childIds]

      const alreadySelected = allIds.every(id => selectedDepartmentIds.includes(id))
      let newDepartmentIds

      if (alreadySelected) {
        newDepartmentIds = selectedDepartmentIds.filter(id => !allIds.includes(id))
      } else {
        const idsToAdd = allIds.filter(id => !selectedDepartmentIds.includes(id))
        newDepartmentIds = [...selectedDepartmentIds, ...idsToAdd]
      }

      let newUserIds = selectedUserIds
      if (assignmentType === 'department_manager') {
        newUserIds = getDepartmentManagers(newDepartmentIds)
        setSelectedUserIds(newUserIds)
      }

      setSelectedDepartmentIds(newDepartmentIds)

      const newValue = {
        type: assignmentType,
        user_ids: assignmentType === 'department_manager' ? newUserIds : selectedUserIds,
        department_ids: newDepartmentIds,
        shared: isShared
      }
      onChange(newValue)
    } else {
      handleDepartmentToggle(departmentId)
    }
  }

  const handleDepartmentToggle = (departmentId) => {
    const department = departments.find(d => d.id === departmentId)
    if (!department) return

    let newDepartmentIds
    if (selectedDepartmentIds.includes(departmentId)) {
      // Deselecting - only remove this specific department, keep children selected
      newDepartmentIds = selectedDepartmentIds.filter(id => id !== departmentId)
    } else {
      // Selecting - add only this department
      newDepartmentIds = [...selectedDepartmentIds, departmentId]
    }
    
    setSelectedDepartmentIds(newDepartmentIds)
    
    // For department_manager assignment, automatically populate user_ids with managers
    let newUserIds = selectedUserIds
    if (assignmentType === 'department_manager') {
      newUserIds = getDepartmentManagers(newDepartmentIds)
      setSelectedUserIds(newUserIds)
    }
    
    const newValue = {
      type: assignmentType,
      user_ids: newUserIds,
      department_ids: newDepartmentIds,
      shared: isShared
    }
    onChange(newValue)
  }

  const handleSharedToggle = () => {
    const newShared = !isShared
    setIsShared(newShared)
    if (assignmentType === 'external_id') {
      onChange(buildExternalIdValue({ shared: newShared }))
      return
    }
    onChange(buildStandardValue({ shared: newShared }))
  }

  const handleIntegrationChange = (integrationId) => {
    const parsedId = integrationId ? parseInt(integrationId, 10) : null
    setSelectedIntegrationId(parsedId)
    onChange(buildExternalIdValue({ integration_id: parsedId }))
  }

  const handleVariableChange = (variableName) => {
    setSelectedVariable(variableName)
    onChange(buildExternalIdValue({ variable: variableName }))
  }

  const getIntegrationName = (integrationId) => {
    const integration = integrations.find((item) => item.id === integrationId)
    return integration?.name || 'Integration'
  }

  const validAvailableVariables = availableVariables.filter(
    (variable) => variable && variable.name && typeof variable.name === 'string'
  )

  // Show shared checkbox when assignment could result in multiple assignees
  const showSharedOption = () => {
    if (assignmentType === 'individual') return selectedUserIds.length >= 2
    if (['department', 'department_manager'].includes(assignmentType)) return getAssignedUsers().length >= 2
    if (assignmentType === 'workflow_assignment' && workflowAssignment) {
      const n = workflowAssignment.user_ids?.length || workflowAssignment.department_ids?.length || 0
      return n >= 2
    }
    if (assignmentType === 'external_id') return true
    return false
  }

  // Get all parent department IDs for a given department
  const getAllParentDepartmentIds = (departmentId) => {
    const department = departments.find(d => d.id === departmentId)
    if (!department || !department.parent_id) return []
    
    const parents = [department.parent_id]
    parents.push(...getAllParentDepartmentIds(department.parent_id))
    return parents
  }

  // Check if a department should be selected (only if directly selected)
  const isDepartmentSelected = (departmentId) => {
    return selectedDepartmentIds.includes(departmentId)
  }

  // Get users that would be assigned based on current department selection
  const getAssignedUsers = () => {
    if (assignmentType === 'department') {
      return users.filter(user => selectedDepartmentIds.includes(user.department_id))
    } else if (assignmentType === 'department_manager') {
      return users.filter(user => 
        selectedDepartmentIds.includes(user.department_id) && 
        user.roles && 
        user.roles.some(role => role.name === 'manager')
      )
    }
    return []
  }


  const getDisplayText = () => {
    if (assignmentType === 'workflow_assignment') {
      if (!workflowAssignment) {
        return 'Use Workflow Assignment'
      }
      
      const type = workflowAssignment.type || 'Not set'
      if (type === 'individual' && workflowAssignment.user_ids?.length > 0) {
        return `Workflow: ${workflowAssignment.user_ids.length} users`
      } else if ((type === 'department' || type === 'department_manager') && workflowAssignment.department_ids?.length > 0) {
        return `Workflow: ${workflowAssignment.department_ids.length} departments`
      }
      return 'Use Workflow Assignment'
    }

    if (assignmentType === 'external_id') {
      if (!selectedIntegrationId || !selectedVariable) {
        return 'Match external user ID...'
      }
      return `${getIntegrationName(selectedIntegrationId)} ← ${selectedVariable}`
    }

    if (assignmentType === 'individual') {
      const selectedUsers = users.filter(user => selectedUserIds.includes(user.id))
      if (selectedUsers.length === 0) {
        return 'Select users...'
      }
      if (selectedUsers.length === 1) {
        return selectedUsers[0].name
      }
      return `${selectedUsers.length} users selected`
    }

    const selectedDepartments = departments.filter(dept => selectedDepartmentIds.includes(dept.id))
    if (selectedDepartments.length === 0) {
      return 'Select departments...'
    }
    if (selectedDepartments.length === 1) {
      return selectedDepartments[0].name
    }
    return `${selectedDepartments.length} departments selected`
  }

  // Organize departments by hierarchy
  const organizeDepartmentsByHierarchy = (depts) => {
    const departmentMap = new Map()
    const rootDepartments = []
    
    // Create a map of all departments
    depts.forEach(dept => {
      departmentMap.set(dept.id, { ...dept, children: [] })
    })
    
    // Build hierarchy
    depts.forEach(dept => {
      if (dept.parent_id) {
        const parent = departmentMap.get(dept.parent_id)
        if (parent) {
          parent.children.push(departmentMap.get(dept.id))
        }
      } else {
        rootDepartments.push(departmentMap.get(dept.id))
      }
    })
    
    // Flatten hierarchy for display with indentation
    const flattenWithIndentation = (dept, level = 0) => {
      const result = [{ ...dept, level, indent: level * 20 }]
      dept.children.forEach(child => {
        result.push(...flattenWithIndentation(child, level + 1))
      })
      return result
    }
    
    return rootDepartments.flatMap(dept => flattenWithIndentation(dept))
  }

  const getSelectedCount = () => {
    switch (assignmentType) {
      case 'individual':
        return selectedUserIds.length
      case 'department':
      case 'department_manager':
        return selectedDepartmentIds.length
      case 'workflow_assignment':
        return 1
      case 'external_id':
        return selectedIntegrationId && selectedVariable ? 1 : 0
      default:
        return 0
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="w-full px-3 py-2 text-left border rounded-md flex items-center justify-between bg-white text-sm hover:bg-gray-50 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {assignmentTypes.find(t => t.value === assignmentType)?.icon}
          <span className="truncate">{getDisplayText()}</span>
          {getSelectedCount() > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
              {getSelectedCount()}
            </span>
          )}
        </div>
        <div 
          className="p-1 -m-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
        >
          <ChevronDown 
            size={14} 
            className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(!isOpen)
            }}
          />
        </div>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="assignment-selector-dropdown absolute left-0 right-0 z-50 mt-1 bg-white border rounded-md shadow-lg dark:bg-[#1f2937] dark:text-white dark:border-gray-600 max-h-96 overflow-y-auto"
          onMouseEnter={(e) => {
            e.stopPropagation()
          }}
          onMouseLeave={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="p-3 border-b dark:border-gray-600">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Assignment Type
            </div>
            <div className="space-y-1">
              {assignmentTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleAssignmentTypeChange(type.value)}
                  className={`w-full p-2 rounded text-left flex items-center gap-2 text-sm ${
                    assignmentType === type.value
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {type.icon}
                  <div className="flex-1">
                    <div className="font-medium">{type.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{type.description}</div>
                  </div>
                  {assignmentType === type.value && (
                    <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {assignmentType === 'individual' && (
            <div className="border-t dark:border-gray-600">
              <div className="p-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 sticky top-0 bg-white dark:bg-[#1f2937] z-10">
                Select Users
              </div>
              <div className="py-1">
                {users.map((user) => {
                  // Normalize user.id to integer for comparison
                  const userId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id
                  const isSelected = selectedUserIds.includes(userId)
                  return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleUserToggle(userId)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                    </div>
                  </button>
                )})}
              </div>
            </div>
          )}

          {['department', 'department_manager'].includes(assignmentType) && (
            <div className="border-t dark:border-gray-600">
              <div className="p-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 sticky top-0 bg-white dark:bg-[#1f2937] z-10">
                Select Departments
              </div>
              <div className="py-1">
                {organizeDepartmentsByHierarchy(departments).map((department) => {
                  const isSelected = isDepartmentSelected(department.id)
                  const departmentHasChildren = hasChildren(department.id)
                  const childIds = departmentHasChildren ? getChildDepartmentIds(department.id) : []
                  const allChildrenSelected = childIds.length > 0 && childIds.every(id => selectedDepartmentIds.includes(id))
                  
                  return (
                    <div key={department.id} className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleDepartmentToggle(department.id)}
                        className={`relative flex-1 px-3 pr-16 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                        style={{ paddingLeft: `${12 + department.indent}px` }}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center gap-2">
                            {department.level > 0 && (
                              <span className="text-gray-400 dark:text-gray-500 text-sm">
                                {'└─ '}
                              </span>
                            )}
                            {department.name}
                          </div>
                          {department.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{department.description}</div>
                          )}
                        </div>

                        {departmentHasChildren && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation()
                              handleParentDepartmentToggle(department.id, true)
                            }}
                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs rounded transition-colors cursor-pointer ${
                              allChildrenSelected && isSelected
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                            title={allChildrenSelected && isSelected ? 'Deselect all child departments' : 'Select all child departments'}
                          >
                            {allChildrenSelected && isSelected ? 'Deselect All' : 'Select All'}
                          </div>
                        )}
                      </button>
                    </div>
                  )
                })}
              </div>
              
              {/* Show assigned users preview */}
              {selectedDepartmentIds.length > 0 && (
                <div className="border-t dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                  <div className="p-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {assignmentType === 'department' ? 'Users to be assigned' : 'Managers to be assigned'} ({getAssignedUsers().length})
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {getAssignedUsers().length > 0 ? (
                        <div className="space-y-1">
                          {getAssignedUsers().slice(0, 8).map((user) => (
                            <div key={user.id} className="flex items-center gap-2 text-sm">
                              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-medium text-blue-700 dark:text-blue-300">
                                {user.firstName?.[0]}{user.lastName?.[0]}
                              </div>
                              <span className="text-gray-900 dark:text-white truncate">
                                {user.firstName} {user.lastName}
                              </span>
                            </div>
                          ))}
                          {getAssignedUsers().length > 8 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                              ... and {getAssignedUsers().length - 8} more
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                          {assignmentType === 'department' ? 'No users found in selected departments' : 'No managers found in selected departments'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {assignmentType === 'external_id' && (
            <div className="border-t dark:border-gray-600 p-3 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Integration
                </label>
                <select
                  value={selectedIntegrationId ?? ''}
                  onChange={(e) => handleIntegrationChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#1f2937] dark:text-white dark:border-gray-600"
                >
                  <option value="">Select integration...</option>
                  {integrations.map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.name}
                      {integration.type === 'mapping' ? ' (mapping)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Workflow variable
                </label>
                {validAvailableVariables.length > 0 ? (
                  <select
                    value={selectedVariable}
                    onChange={(e) => handleVariableChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#1f2937] dark:text-white dark:border-gray-600"
                  >
                    <option value="">Select variable...</option>
                    {validAvailableVariables.map((variable) => (
                      <option key={variable.name} value={variable.name}>
                        {variable.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    No upstream variables. Add an HTTP Request step that extracts variables before this task.
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                At runtime, the variable value is matched against external user IDs for the selected integration.
                If no user matches, the task is assigned to the workflow owner.
              </p>
            </div>
          )}

          {showSharedOption() && (
            <div className="border-t dark:border-gray-600 p-3">
              <button
                type="button"
                onClick={handleSharedToggle}
                className="w-full flex items-center gap-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-2 -m-1"
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  isShared ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {isShared && <Check className="h-3 w-3 text-white" />}
                </div>
                <UsersRound className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-700 dark:text-gray-300">Shared task</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">One completion completes for everyone</div>
                </div>
              </button>
            </div>
          )}

          {assignmentType === 'workflow_assignment' && (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              This task will use the workflow's mass assignment settings when created.
            </div>
          )}

          {assignmentType === 'workflow_assignment' && workflowAssignment && (
            <div className="border-t dark:border-gray-600 p-3">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Workflow Assignment Details
              </div>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="font-medium">Type:</span> 
                  <span className="ml-2 capitalize">{workflowAssignment.type || 'Not set'}</span>
                </div>
                
                {workflowAssignment.type === 'individual' && workflowAssignment.user_ids && workflowAssignment.user_ids.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">Assigned Users:</span>
                    <div className="mt-1 space-y-1">
                      {workflowAssignment.user_ids.map(userId => {
                        const user = users.find(u => u.id === userId)
                        return user ? (
                          <div key={userId} className="ml-4 text-gray-600 dark:text-gray-400">
                            • {user.firstName} {user.lastName}
                          </div>
                        ) : (
                          <div key={userId} className="ml-4 text-gray-500 dark:text-gray-500">
                            • User ID: {userId} (not found)
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {(workflowAssignment.type === 'department' || workflowAssignment.type === 'department_manager') && 
                 workflowAssignment.department_ids && workflowAssignment.department_ids.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">Assigned Departments:</span>
                    <div className="mt-1 space-y-1">
                      {workflowAssignment.department_ids.map(deptId => {
                        const dept = departments.find(d => d.id === deptId)
                        return dept ? (
                          <div key={deptId} className="ml-4 text-gray-600 dark:text-gray-400">
                            • {dept.full_path || dept.name}
                          </div>
                        ) : (
                          <div key={deptId} className="ml-4 text-gray-500 dark:text-gray-500">
                            • Department ID: {deptId} (not found)
                          </div>
                        )
                      })}
                    </div>
                    {workflowAssignment.type === 'department_manager' && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Only managers of these departments will be assigned
                      </div>
                    )}
                    {workflowAssignment.type === 'department' && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        All users in these departments will be assigned
                      </div>
                    )}
                  </div>
                )}
                
                {(!workflowAssignment.user_ids || workflowAssignment.user_ids.length === 0) && 
                 (!workflowAssignment.department_ids || workflowAssignment.department_ids.length === 0) && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No specific assignments configured for this workflow
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AssignmentSelector
